import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { X, ZoomIn, ZoomOut, RotateCcw, Maximize2, ChevronLeft, ChevronRight } from "lucide-react";

interface Real3DFlipbookProps {
	type: "2023" | "bachelor" | "master";
	onClose: () => void;
}

export default function Real3DFlipbook({ type, onClose }: Real3DFlipbookProps) {
	const flipbookRef = useRef<HTMLDivElement>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [isFlipping, setIsFlipping] = useState(false);
	const [zoom, setZoom] = useState(1);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [imagesLoaded, setImagesLoaded] = useState(false);

	// Detect mobile device
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const getTemplateData = () => {
		switch (type) {
			case "2023":
				return {
					title: "Mẫu Văn Bằng 2023",
					images: [
						"/degree/2026/bacsinhakhoa.png",
						"/degree/2026/bacsiykhoa.png",
						"/degree/2026/cunhan.png",
						"/degree/2026/duocsi.png",
						"/degree/2026/kientrucsu.png",
						"/degree/2026/kysu.png",
						"/degree/2026/thacsi.png",
						"/degree/2026/tiensi.png",
					],
				};
			case "bachelor":
				return {
					title: "Mẫu Văn Bằng Đại Học",
					images: [
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0001.jpg",
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0002.jpg",
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0003.jpg",
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0004.jpg",
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0005.jpg",
						"/degree/bachelor/mau_bang_dai_hoc_truoc_2023_pages-to-jpg-0006.jpg",
					],
				};
			case "master":
				return {
					title: "Mẫu Văn Bằng Thạc sĩ",
					images: ["/degree/master/mau_bang_cao_hoc_truoc_2023_page-0001.jpg", "/degree/master/mau_bang_cao_hoc_truoc_2023_page-0002.jpg"],
				};
			default:
				return {
					title: "Mẫu Văn Bằng",
					images: ["https://placehold.co/600x400?text=Chưa+có"],
				};
		}
	};

	const templateData = getTemplateData();

	// Preload images
	useEffect(() => {
		const imagePromises = templateData.images.map((src) => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.onload = resolve;
				img.onerror = reject;
				img.src = src;
			});
		});

		Promise.all(imagePromises)
			.then(() => setImagesLoaded(true))
			.catch(() => {
				console.warn("Some images failed to load, continuing anyway");
				setImagesLoaded(true);
			});
	}, [type, templateData.images]);

	const flipToPage = (pageIndex: number) => {
		if (pageIndex === currentPage || isFlipping) return;

		setIsFlipping(true);
		setCurrentPage(pageIndex);

		// Simulate flip animation duration
		setTimeout(() => {
			setIsFlipping(false);
		}, 0);
	};

	const nextPage = () => {
		if (currentPage < templateData.images.length - 1 && !isFlipping) {
			flipToPage(currentPage + 1);
		}
	};

	const prevPage = () => {
		if (currentPage > 0 && !isFlipping) {
			flipToPage(currentPage - 1);
		}
	};

	const zoomIn = () => {
		setZoom((prev) => Math.min(prev + 0.2, 2));
	};

	const zoomOut = () => {
		setZoom((prev) => Math.max(prev - 0.2, 0.6));
	};

	const resetZoom = () => {
		setZoom(1);
	};

	const toggleFullscreen = () => {
		setIsFullscreen(!isFullscreen);
	};

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (isFlipping) return; // Prevent key presses during animation

			switch (e.key) {
				case "ArrowRight":
				case " ":
					e.preventDefault();
					if (currentPage < templateData.images.length - 1) {
						flipToPage(currentPage + 1);
					}
					break;
				case "ArrowLeft":
					e.preventDefault();
					if (currentPage > 0) {
						flipToPage(currentPage - 1);
					}
					break;
				case "Escape":
					e.preventDefault();
					onClose();
					break;
				case "+":
				case "=":
					e.preventDefault();
					setZoom((prev) => Math.min(prev + 0.2, 2));
					break;
				case "-":
					e.preventDefault();
					setZoom((prev) => Math.max(prev - 0.2, 0.6));
					break;
				case "0":
					e.preventDefault();
					setZoom(1);
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [currentPage, templateData.images.length, isFlipping, onClose, flipToPage]);

	// Handle touch gestures for mobile
	useEffect(() => {
		if (!isMobile || !flipbookRef.current) return;

		let startX = 0;
		let startY = 0;
		let isDragging = false;

		const handleTouchStart = (e: TouchEvent) => {
			startX = e.touches[0].clientX;
			startY = e.touches[0].clientY;
			isDragging = true;
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (!isDragging) return;
			e.preventDefault(); // Prevent scrolling during swipe
		};

		const handleTouchEnd = (e: TouchEvent) => {
			if (!isDragging || !e.changedTouches[0]) return;
			isDragging = false;

			const endX = e.changedTouches[0].clientX;
			const endY = e.changedTouches[0].clientY;
			const diffX = startX - endX;
			const diffY = startY - endY;

			// Only process horizontal swipes with minimum distance
			if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
				if (diffX > 0 && currentPage < templateData.images.length - 1) {
					// Swipe left - next page
					nextPage();
				} else if (diffX < 0 && currentPage > 0) {
					// Swipe right - previous page
					prevPage();
				}
			}
		};

		const element = flipbookRef.current;
		element.addEventListener("touchstart", handleTouchStart, { passive: true });
		element.addEventListener("touchmove", handleTouchMove, { passive: false });
		element.addEventListener("touchend", handleTouchEnd, { passive: true });

		return () => {
			element.removeEventListener("touchstart", handleTouchStart);
			element.removeEventListener("touchmove", handleTouchMove);
			element.removeEventListener("touchend", handleTouchEnd);
		};
	}, [isMobile, currentPage, templateData.images.length, nextPage, prevPage]);

	// Loading state
	if (!imagesLoaded) {
		return (
			<div className='fixed inset-0 bg-black/90 z-[100] flex items-center justify-center'>
				<div className='bg-white rounded-lg p-6 max-w-sm mx-4'>
					<div className='flex items-center gap-3'>
						<div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary'></div>
						<div>
							<h3 className='font-semibold'>Đang tải mẫu văn bằng...</h3>
							<p className='text-sm text-muted-foreground'>{templateData.title}</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`fixed inset-0 bg-black/90 z-[9999] ${isFullscreen || isMobile ? "p-0" : "p-4"} ${isMobile ? "flipbook-mobile" : ""}`}>
			<div className={`bg-gradient-to-br from-slate-50 to-slate-100 ${isFullscreen || isMobile ? "h-full w-full" : "rounded-xl h-full"} flex flex-col shadow-2xl overflow-hidden`}>
				{/* Header Controls */}
				<div className='flex items-center justify-between p-2 sm:p-3 md:p-4 bg-white/95 backdrop-blur-sm border-b border-slate-200/50 flex-shrink-0'>
					<div className='flex items-center gap-2 md:gap-4 min-w-0 flex-1'>
						<div className='min-w-0 flex-1'>
							<h2 className='text-base sm:text-lg md:text-xl font-semibold text-slate-800 truncate'>{templateData.title}</h2>
							<p className='text-xs md:text-sm text-slate-600'>
								Trang {currentPage + 1} / {templateData.images.length}
							</p>
						</div>
					</div>

					<div className='flex items-center gap-1 md:gap-2 flex-shrink-0'>
						{!isMobile && (
							<>
								<Button variant='outline' size='sm' onClick={zoomOut} disabled={zoom <= 0.6}>
									<ZoomOut className='w-4 h-4' />
								</Button>
								<Button variant='outline' size='sm' onClick={resetZoom}>
									<RotateCcw className='w-4 h-4' />
								</Button>
								<span className='text-xs px-2 py-1 bg-slate-100 rounded hidden sm:inline'>{Math.round(zoom * 100)}%</span>
								<Button variant='outline' size='sm' onClick={zoomIn} disabled={zoom >= 2}>
									<ZoomIn className='w-4 h-4' />
								</Button>
								<Button variant='outline' size='sm' onClick={toggleFullscreen}>
									<Maximize2 className='w-4 h-4' />
								</Button>
							</>
						)}
						<Button variant='outline' size='sm' onClick={onClose} className='min-w-[40px]'>
							<X className='w-4 h-4' />
						</Button>
					</div>
				</div>

				{/* 3D Flipbook Container */}
				<div className='flex-1 overflow-hidden relative bg-slate-50'>
					<div ref={flipbookRef} className='w-full h-full flex items-center justify-center p-2 sm:p-4 md:p-8' style={{ perspective: isMobile ? "800px" : "1200px" }}>
						<div
							className='relative transition-transform duration-0 ease-out'
							style={{
								transform: `scale(${isMobile ? Math.min(zoom * 0.9, 1) : zoom})`,
								transformStyle: "preserve-3d",
							}}
						>
							{/* Book Container */}
							<div className={`relative ${isMobile ? "w-[85vw] h-[50vh] min-h-[300px] max-w-[400px] max-h-[500px]" : "w-[800px] h-[600px] max-w-full max-h-full"} mx-auto`}>
								{/* Current Page */}
								<div
									className={`absolute inset-0 transition-all duration-0 ease-in-out ${isFlipping ? "animate-pulse" : ""}`}
									style={{
										transform: isFlipping ? "rotateY(-5deg) translateZ(10px)" : "rotateY(0deg) translateZ(0px)",
										transformOrigin: "left center",
										transformStyle: "preserve-3d",
									}}
								>
									{/* Page Front */}
									<div className='absolute inset-0 bg-white rounded-lg shadow-2xl border border-slate-200'>
										<img
											src={templateData.images[currentPage]}
											alt={`${templateData.title} - Trang ${currentPage + 1}`}
											className='w-full h-full object-contain rounded-lg'
											draggable={false}
											onError={(e) => {
												console.warn("Image failed to load:", templateData.images[currentPage]);
												// Set a fallback background with text
												e.currentTarget.style.display = "none";
												e.currentTarget.parentElement!.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
												e.currentTarget.parentElement!.innerHTML += `
                          <div style="position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #64748b; text-align: center; padding: 2rem;">
                            <div style="font-size: 3rem; margin-bottom: 1rem;">📜</div>
                            <h3 style="font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;">${templateData.title}</h3>
                            <p style="font-size: 1rem;">Trang ${currentPage + 1} / ${templateData.images.length}</p>
                            <div style="margin-top: 2rem; padding: 1rem; background: white; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                              <p style="font-size: 0.875rem; color: #475569;">Mẫu văn bằng của Đại học Văn Lang</p>
                              <p style="font-size: 0.75rem; color: #94a3b8; margin-top: 0.5rem;">Hình ảnh mẫu đang được tải...</p>
                            </div>
                          </div>
                        `;
											}}
										/>
									</div>

									{/* Page Shadow */}
									<div
										className='absolute inset-0 bg-gradient-to-r from-transparent via-slate-400/20 to-slate-600/40 rounded-lg pointer-events-none'
										style={{
											opacity: isFlipping ? 0.3 : 0,
											transition: "opacity 0.4s ease-in-out",
										}}
									/>
								</div>

								{/* Next Page Preview (visible during flip) */}
								{currentPage < templateData.images.length - 1 && (
									<div
										className='absolute inset-0 bg-white rounded-lg shadow-xl border border-slate-200'
										style={{
											transform: "translateZ(-5px)",
											opacity: isFlipping ? 0.7 : 0,
											transition: "opacity 0.4s ease-in-out",
										}}
									>
										<img
											src={templateData.images[currentPage + 1]}
											alt={`${templateData.title} - Trang ${currentPage + 2}`}
											className='w-full h-full object-contain rounded-lg'
											draggable={false}
											onError={(e) => {
												e.currentTarget.style.display = "none";
												e.currentTarget.parentElement!.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
												e.currentTarget.parentElement!.innerHTML += `
                          <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #94a3b8;">
                            <div style="text-align: center;">
                              <div style="font-size: 2rem; margin-bottom: 0.5rem;">📄</div>
                              <p style="font-size: 0.875rem;">Trang ${currentPage + 2}</p>
                            </div>
                          </div>
                        `;
											}}
										/>
									</div>
								)}

								{/* Interactive Areas for Page Turning */}
								<div className='absolute inset-0 flex pointer-events-auto'>
									{/* Left Click Area */}
									<div
										className='w-1/3 h-full cursor-pointer flex items-center justify-start pl-2 md:pl-4 group active:scale-95 transition-transform'
										onClick={prevPage}
										style={{ touchAction: "manipulation" }}
									>
										<div className={`${isMobile ? "opacity-70 scale-110" : "opacity-0 group-hover:opacity-100"} transition-all bg-black/30 backdrop-blur-sm rounded-full p-2 md:p-3`}>
											<ChevronLeft className='w-5 h-5 md:w-8 md:h-8 text-white' />
										</div>
									</div>

									{/* Right Click Area */}
									<div
										className='flex-1 h-full cursor-pointer flex items-center justify-end pr-2 md:pr-4 group active:scale-95 transition-transform'
										onClick={nextPage}
										style={{ touchAction: "manipulation" }}
									>
										<div className={`${isMobile ? "opacity-70 scale-110" : "opacity-0 group-hover:opacity-100"} transition-all bg-black/30 backdrop-blur-sm rounded-full p-2 md:p-3`}>
											<ChevronRight className='w-5 h-5 md:w-8 md:h-8 text-white' />
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Bottom Navigation */}
				<div className='p-2 sm:p-3 md:p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200/50 flex-shrink-0'>
					<div className='flex items-center justify-center gap-3 md:gap-6'>
						{/* Mobile Navigation Buttons */}
						{isMobile && (
							<div className='flex items-center gap-3 w-full justify-between'>
								<Button
									variant='outline'
									size='sm'
									onClick={prevPage}
									disabled={currentPage === 0 || isFlipping}
									className='flex items-center gap-1 min-w-[80px] touch-manipulation'
									style={{ touchAction: "manipulation" }}
								>
									<ChevronLeft className='w-4 h-4' />
									Trước
								</Button>

								<div className='text-sm font-medium px-3 py-2 bg-slate-100 rounded-md flex-shrink-0'>
									{currentPage + 1} / {templateData.images.length}
								</div>

								<Button
									variant='outline'
									size='sm'
									onClick={nextPage}
									disabled={currentPage === templateData.images.length - 1 || isFlipping}
									className='flex items-center gap-1 min-w-[80px] touch-manipulation'
									style={{ touchAction: "manipulation" }}
								>
									Sau
									<ChevronRight className='w-4 h-4' />
								</Button>
							</div>
						)}

						{/* Desktop Page Thumbnails */}
						{!isMobile && (
							<div className='flex gap-3'>
								{templateData.images.map((image, index) => (
									<button
										key={index}
										onClick={() => flipToPage(index)}
										disabled={isFlipping}
										className={`relative group transition-all duration-0 ${currentPage === index ? "ring-2 ring-primary ring-offset-2 scale-110" : "hover:scale-105 hover:shadow-lg"}`}
									>
										<div className='w-16 h-12 bg-white rounded border-2 border-slate-200 overflow-hidden shadow-sm'>
											<img
												src={image}
												alt={`Trang ${index + 1}`}
												className='w-full h-full object-cover'
												onError={(e) => {
													e.currentTarget.style.display = "none";
													e.currentTarget.parentElement!.style.background = "#f1f5f9";
													e.currentTarget.parentElement!.innerHTML += `
                            <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #64748b;">
                              <span style="font-size: 0.75rem;">📄</span>
                            </div>
                          `;
												}}
											/>
										</div>
										<div className='absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-xs font-medium text-slate-600'>{index + 1}</div>
									</button>
								))}
							</div>
						)}
					</div>

					{/* Keyboard Shortcuts Hint - Desktop Only */}
					{!isMobile && (
						<div className='text-center mt-3'>
							<p className='text-xs text-slate-500'>Sử dụng ← → để lật trang • Spacebar để trang sau • ESC để đóng • +/- để zoom</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
